import { apiClient } from './apiClient';
import type {
  BuyerOrder,
  DesignerOffer,
  Project,
  ProjectStatus,
  Milestone,
  OrderBrief,
  ContactInfo,
  OfferBreakdown,
  MatchedDesigner,
} from '../types/commissions.types';
import { computePricing } from '../types/commissions.types';

// ── Raw API row shapes (snake_case from backend) ────────────────────────────

/* eslint-disable @typescript-eslint/no-explicit-any */

// ── Data mappers ────────────────────────────────────────────────────────────

function mapOrder(raw: any): BuyerOrder {
  return {
    id: raw.id,
    buyerId: raw.buyer_id,
    status: raw.status,
    brief: {
      occasion: raw.occasion ?? '',
      occasionId: raw.occasion_id ?? '',
      monthLabel: raw.month_label ?? '',
      eventDate: raw.event_date ?? undefined,
      note: raw.note ?? undefined,
      styleRefs: raw.style_refs ?? [],
      fabricChoice: raw.fabric_choice ?? '',
      fabricImages: raw.fabric_images ?? [],
    },
    contact: {
      fullName: raw.contact_name ?? '',
      email: raw.contact_email ?? '',
      phone: raw.contact_phone ?? '',
      phoneIsWhatsapp: raw.contact_phone_is_whatsapp ?? false,
      // Optional geo from rfq_orders.iplocation jsonb — supports a few common
      // shapes (ipapi, ip-api.com, cloudflare) so we don't lock the backend
      // into one provider before it starts populating the column.
      city: raw.iplocation?.city ?? raw.iplocation?.locality ?? undefined,
      country: raw.iplocation?.country_name ?? raw.iplocation?.country ?? undefined,
      countryCode: (raw.iplocation?.country_code ?? raw.iplocation?.countryCode ?? '')
        .toString()
        .toUpperCase() || undefined,
    },
    createdAtISO: raw.created_at ?? '',
    acceptedOfferId: raw.accepted_offer_id ?? undefined,
    projectId: raw.project_id ?? undefined,
    // Present only on the designer's incoming-briefs read (per-row source).
    matchSource: raw.match_source ?? undefined,
    // Order-level origin (present on reads that select it).
    source: raw.source ?? undefined,
    designerStatus: raw.designer_status ?? undefined,
  };
}

function mapDesignerProfile(raw: any, fallbackId?: string) {
  return {
    id: raw?.id ?? fallbackId ?? '',
    name: raw?.full_name ?? raw?.name ?? '',
    handle: raw?.username ?? raw?.handle ?? '',
    avatar: raw?.profile_picture ?? raw?.avatar ?? '',
    city: raw?.city ?? '',
    verified: raw?.is_verified ?? raw?.verified ?? false,
    rating: 0,
    reviewCount: 0,
    portfolio: [] as string[],
  };
}

function mapOffer(raw: any): DesignerOffer {
  const hasCounter = raw.counter_total != null || raw.counter_note;
  return {
    id: raw.id,
    orderId: raw.order_id ?? raw.orderId ?? '',
    designer: mapDesignerProfile(raw.designer, raw.designer_id),
    // Backend's mapOfferStatus() now maps DB 'pending' → 'submitted' etc.
    status: raw.status ?? 'submitted',
    total: Number(raw.total) ?? 0,
    breakdown: raw.breakdown ?? { designFee: 0, fabricEstimate: 0, delivery: 0 },
    leadTimeDays: raw.lead_time_days ?? raw.leadTimeDays ?? 0,
    readyByISO: raw.ready_by ?? raw.created_at ?? '',
    inclusions: raw.inclusions ?? [],
    pitch: raw.pitch ?? '',
    submittedAtISO: raw.created_at ?? '',
    expiresAtISO: raw.expires_at ?? raw.created_at ?? '',
    threadId: raw.thread_id ?? '',
    editedAtISO: raw.updated_at && raw.updated_at !== raw.created_at ? raw.updated_at : undefined,
    counter: hasCounter
      ? {
          askedAtISO: raw.updated_at ?? raw.created_at ?? '',
          message: raw.counter_note ?? '',
          proposedTotal: raw.counter_total != null ? Number(raw.counter_total) : undefined,
        }
      : undefined,
    // Attach the order summary when present (designer's offers list).
    // Now includes occasion, month_label, contact_name from the backend join.
    ...(raw.order ? {
      _order: {
        id: raw.order.id,
        orderNumber: raw.order.order_number,
        occasion: raw.order.occasion ?? '',
        monthLabel: raw.order.month_label ?? '',
        eventDate: raw.order.event_date ?? undefined,
        contactName: raw.order.contact_name ?? '',
        status: raw.order.status,
      }
    } : {}),
  } as DesignerOffer;
}

// Production stage definitions — matches the STAGES array used in the UI
const PRODUCTION_STAGES = [
  { key: 'fabric_sourced',      label: 'Fabric sourced' },
  { key: 'cutting',             label: 'Cutting' },
  { key: 'sewing',              label: 'Sewing' },
  { key: 'first_fitting',       label: 'First fitting' },
  { key: 'alterations',         label: 'Alterations' },
  { key: 'finishing',           label: 'Finishing touches' },
  { key: 'quality_check',       label: 'Quality check' },
  { key: 'ready_for_delivery',  label: 'Ready for delivery' },
];

function mapProjectStatus(status: string): ProjectStatus {
  const map: Record<string, ProjectStatus> = {
    deposit_pending: 'awaiting_deposit',
    active: 'in_production',
    delivered: 'delivered',
    completed: 'completed',
    disputed: 'disputed',
  };
  return (map[status] as ProjectStatus) ?? 'in_production';
}

function mapProject(raw: any): Project {
  const agreedTotal = Number(raw.agreed_total ?? 0);
  const pricing = computePricing(agreedTotal);
  const depositAmount = Number(raw.deposit_amount ?? pricing.depositDue);
  const balanceAmount = Number(raw.balance_amount ?? pricing.grand - depositAmount);
  // Backend may return orderId either as a direct column OR inside the joined order{}
  const orderId = raw.order_id ?? raw.order?.id ?? '';

  const milestones: Milestone[] = [
    {
      key: 'deposit',
      label: 'Deposit (70%)',
      amount: depositAmount,
      state: raw.deposit_paid ? (raw.balance_paid ? 'released' : 'paid') : 'due',
      paidAtISO: raw.deposit_paid ? raw.updated_at : undefined,
      releasedAtISO: raw.balance_paid ? raw.updated_at : undefined,
    },
    {
      key: 'completion',
      label: 'Balance (30%)',
      amount: balanceAmount,
      state: raw.balance_paid ? 'released' : 'due',
      releasedAtISO: raw.balance_paid ? raw.updated_at : undefined,
    },
  ];

  return {
    id: raw.id,
    orderId,
    buyerId: raw.buyer_id ?? '',
    // Backend now reliably joins designer{} via fetchProjectFull / listBuyerProjects
    designer: mapDesignerProfile(raw.designer ?? raw.buyer, raw.designer_id),
    status: mapProjectStatus(raw.status),
    total: agreedTotal,
    protectionFee: computePricing(agreedTotal).protectionFee,
    milestones,
    timeline: (() => {
      const doneStages: string[] = raw.done_stages ?? [];
      const firstIncompleteIdx = PRODUCTION_STAGES.findIndex(s => !doneStages.includes(s.key));
      return PRODUCTION_STAGES.map((s, i) => ({
        key: s.key as any,
        label: s.label,
        done: doneStages.includes(s.key),
        active: i === firstIncompleteIdx,
        doneAtISO: doneStages.includes(s.key) ? (raw.updated_at ?? undefined) : undefined,
        etaISO: undefined,
      }));
    })(),
    updates: (raw.updates ?? []).map((u: any) => ({
      id: u.id,
      authorId: u.designer_id ?? '',
      // Backend now joins designer profile on updates via fetchProjectFull
      authorName: u.designer?.full_name ?? u.author_name ?? '',
      authorAvatar: u.designer?.profile_picture ?? u.author_avatar ?? '',
      body: u.body ?? '',
      media: u.media ?? [],
      createdAtISO: u.created_at ?? '',
    })),
    threadId: raw.thread_id ?? '',
    createdAtISO: raw.created_at ?? '',
    etaISO: raw.eta ?? '',
    extensionRequest: raw.extension_status
      ? {
          proposedEtaISO: raw.extension_requested_eta ?? '',
          reason: raw.extension_reason ?? '',
          status: raw.extension_status,
          requestedAtISO: raw.updated_at ?? '',
        }
      : undefined,
  };
}

// ── Request / response shapes ───────────────────────────────────────────

export interface SubmitOrderInput {
  brief: OrderBrief;
  contact: ContactInfo;
  /**
   * Direct booking via a designer's personal order link. Username or user id —
   * the backend resolves it (username first, then id) and books the brief
   * straight to that designer instead of running the matching fan-out. Omit
   * for a normal matched request. Invalid/unresolvable → backend falls back
   * to matching.
   */
  targetDesigner?: string;
}

export interface SubmitOfferInput {
  total: number;
  breakdown: OfferBreakdown;
  leadTimeDays: number;
  inclusions: string[];
  pitch: string;
  currency?: string;
}

export interface UpdateOfferInput {
  breakdown: OfferBreakdown;
  leadTimeDays: number;
  inclusions: string[];
  pitch: string;
}

export interface CounterOfferInput {
  message: string;
  proposedTotal?: number;
  proposedLeadTimeDays?: number;
}

export interface AcceptOfferResponse {
  project: Project;
  payment: {
    depositAmount: number;
    reference: string;
    authorizationUrl: string;
  };
}

export interface AddUpdateInput {
  body: string;
  media: string[];
}

export interface RequestExtensionInput {
  proposedEta: string;
  reason: string;
}

export interface UploadMediaResponse {
  urls: string[];
}

// ── Service ─────────────────────────────────────────────────────────────

export const rfqService = {

  // ── Orders (buyer) ─────────────────────────────────────────────────

  /** POST /rfq/orders — buyer submits a brief (guest-friendly, no auth required) */
  async submitOrder(input: SubmitOrderInput): Promise<BuyerOrder> {
    const res = await apiClient.post<{ order: any }>('/rfq/orders', input, { skipAuth: true });
    if (!res.success) throw new Error(res.error?.message ?? 'Failed to submit order');
    return mapOrder(res.data.order);
  },

  /** GET /rfq/orders — buyer's own orders */
  async listOrders(): Promise<BuyerOrder[]> {
    const res = await apiClient.get<{ orders: any[] }>('/rfq/orders');
    if (!res.success) throw new Error(res.error.message);
    return (res.data.orders ?? []).map(mapOrder);
  },

  /** GET /rfq/orders/:id */
  async getOrder(orderId: string): Promise<BuyerOrder> {
    const res = await apiClient.get<{ order: any }>(`/rfq/orders/${orderId}`);
    if (!res.success) throw new Error(res.error.message);
    return mapOrder(res.data.order);
  },

  /** GET /rfq/orders/:id/offers */
  async listOffers(orderId: string): Promise<DesignerOffer[]> {
    const res = await apiClient.get<{ offers: any[] }>(`/rfq/orders/${orderId}/offers`);
    if (!res.success) throw new Error(res.error.message);
    return (res.data.offers ?? []).map(mapOffer);
  },

  /**
   * GET /rfq/orders/:id/matched-designers — the rfq_enabled designers the
   * backend's matching logic routed this brief to. Used to replace vague
   * "matching…" copy with real designer cards while offers are pending.
   * Pass email for guest access (no auth token needed).
   */
  async listMatchedDesigners(orderId: string, guestEmail?: string): Promise<MatchedDesigner[]> {
    const params = guestEmail ? { email: guestEmail } : undefined;
    const res = await apiClient.get<{ designers: any[] }>(`/rfq/orders/${orderId}/matched-designers`, params);
    if (!res.success) throw new Error(res.error.message);
    return (res.data.designers ?? []).map((d: any) => ({
      ...mapDesignerProfile(d, d.id),
      status: d.status ?? undefined,
    }));
  },

  /** POST /rfq/orders/:id/accept — accept an offer and initiate payment */
  async acceptOffer(orderId: string, offerId: string): Promise<AcceptOfferResponse> {
    const res = await apiClient.post<any>(`/rfq/orders/${orderId}/accept`, { offerId });
    if (!res.success) throw new Error(res.error.message);
    return {
      project: mapProject(res.data.project),
      payment: res.data.payment ?? { depositAmount: 0, reference: '', authorizationUrl: '' },
    };
  },

  /** POST /rfq/orders/:id/decline — designer passes on a brief */
  async declineOrder(orderId: string, reason?: string): Promise<void> {
    const res = await apiClient.post(`/rfq/orders/${orderId}/decline`, { reason });
    if (!res.success) throw new Error(res.error.message);
  },

  // ── Offers ─────────────────────────────────────────────────────────

  /** POST /rfq/orders/:id/offers — designer submits an offer */
  async submitOffer(orderId: string, input: SubmitOfferInput): Promise<DesignerOffer> {
    const res = await apiClient.post<{ offer: any }>(`/rfq/orders/${orderId}/offers`, input);
    if (!res.success) throw new Error(res.error.message);
    return mapOffer(res.data.offer);
  },

  /** PATCH /rfq/offers/:id — designer edits an offer */
  async updateOffer(offerId: string, input: UpdateOfferInput): Promise<DesignerOffer> {
    const res = await apiClient.patch<{ offer: any }>(`/rfq/offers/${offerId}`, input);
    if (!res.success) throw new Error(res.error.message);
    return mapOffer(res.data.offer);
  },

  /** POST /rfq/offers/:id/withdraw */
  async withdrawOffer(offerId: string): Promise<DesignerOffer> {
    const res = await apiClient.post<{ offer: any }>(`/rfq/offers/${offerId}/withdraw`);
    if (!res.success) throw new Error(res.error.message);
    return mapOffer(res.data.offer);
  },

  /** POST /rfq/offers/:id/accept-counter — designer accepts buyer's counter price */
  async acceptCounter(offerId: string): Promise<DesignerOffer> {
    const res = await apiClient.post<{ offer: any }>(`/rfq/offers/${offerId}/accept-counter`);
    if (!res.success) throw new Error(res.error.message);
    return mapOffer(res.data.offer);
  },

  /** POST /rfq/offers/:id/decline-counter — designer declines buyer's counter price */
  async declineCounter(offerId: string): Promise<DesignerOffer> {
    const res = await apiClient.post<{ offer: any }>(`/rfq/offers/${offerId}/decline-counter`);
    if (!res.success) throw new Error(res.error.message);
    return mapOffer(res.data.offer);
  },

  /**
   * GET /rfq/offers/:id/invoice.pdf — server-rendered PDF invoice.
   * Returns a Blob; caller is responsible for triggering the download.
   * Auth: buyer (owns order), designer (owns offer), or admin.
   */
  async downloadInvoicePdf(offerId: string): Promise<{ blob: Blob; filename: string }> {
    const token =
      localStorage.getItem('accessToken') || localStorage.getItem('token') || '';
    const url = `${apiClient.getBaseUrl()}/rfq/offers/${offerId}/invoice.pdf`;
    const res = await fetch(url, {
      method: 'GET',
      headers: {
        Accept: 'application/pdf',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
    });
    if (!res.ok) {
      let message = 'Failed to generate invoice PDF';
      try {
        const j = await res.json();
        message = j?.error?.message || message;
      } catch {
        /* non-json response */
      }
      throw new Error(message);
    }
    const blob = await res.blob();
    // Derive filename from Content-Disposition, fall back to offer id.
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    const filename = match?.[1] ? decodeURIComponent(match[1]) : `invoice-${offerId.slice(0, 8)}.pdf`;
    return { blob, filename };
  },

  /** POST /rfq/offers/:id/counter — buyer sends a counter offer */
  async makeOffer(offerId: string, input: CounterOfferInput): Promise<DesignerOffer> {
    // API expects { counterTotal, counterNote } — map from frontend shape
    const body: Record<string, any> = { counterNote: input.message };
    if (input.proposedTotal != null && input.proposedTotal > 0) {
      body.counterTotal = input.proposedTotal;
    }
    const res = await apiClient.post<{ offer: any }>(`/rfq/offers/${offerId}/counter`, body);
    if (!res.success) {
      const err = new Error(res.error.message) as Error & { appCode?: string };
      err.appCode = res.error.appCode;
      throw err;
    }
    return mapOffer(res.data.offer);
  },

  // ── Designer discovery ──────────────────────────────────────────────

  /** POST /rfq/orders/:id/accept-brief — designer accepts an assigned brief, unlocking contact info */
  async acceptBrief(orderId: string): Promise<BuyerOrder> {
    const res = await apiClient.post<{ order: any }>(`/rfq/orders/${orderId}/accept-brief`);
    if (!res.success) throw new Error(res.error.message);
    return mapOrder(res.data.order);
  },

  /** GET /rfq/designer/incoming-briefs — briefs this designer hasn't quoted yet */
  async listIncomingBriefs(): Promise<BuyerOrder[]> {
    const res = await apiClient.get<{ briefs: any[] }>('/rfq/designer/incoming-briefs');
    if (!res.success) throw new Error(res.error.message);
    return (res.data.briefs ?? []).map(mapOrder);
  },

  /** GET /rfq/designer/offers — all offers submitted by this designer */
  async listDesignerOffers(): Promise<DesignerOffer[]> {
    const res = await apiClient.get<{ offers: any[] }>('/rfq/designer/offers');
    if (!res.success) throw new Error(res.error.message);
    return (res.data.offers ?? []).map(mapOffer);
  },

  /** GET /rfq/designer/projects — designer's active + completed projects */
  async listDesignerProjects(): Promise<Project[]> {
    const res = await apiClient.get<{ projects: any[] }>('/rfq/designer/projects');
    if (!res.success) throw new Error(res.error.message);
    return (res.data.projects ?? []).map(mapProject);
  },

  /** GET /rfq/buyer/projects — buyer's active + completed projects */
  async listBuyerProjects(): Promise<Project[]> {
    const res = await apiClient.get<{ projects: any[] }>('/rfq/buyer/projects');
    if (!res.success) throw new Error(res.error.message);
    return (res.data.projects ?? []).map(mapProject);
  },

  // ── Projects ────────────────────────────────────────────────────────

  /** GET /rfq/projects/:id */
  async getProject(projectId: string): Promise<Project> {
    const res = await apiClient.get<{ project: any }>(`/rfq/projects/${projectId}`);
    if (!res.success) throw new Error(res.error.message);
    return mapProject(res.data.project);
  },

  /** GET /rfq/projects/by-thread/:threadId — returns { project: null } (200) if none linked */
  async getProjectByThreadId(threadId: string): Promise<Project | null> {
    const res = await apiClient.get<{ project: any }>(`/rfq/projects/by-thread/${threadId}`);
    if (!res.success) return null;
    return res.data.project ? mapProject(res.data.project) : null;
  },

  /** POST /rfq/projects/:id/updates — designer posts a progress update */
  async addProjectUpdate(projectId: string, input: AddUpdateInput): Promise<Project> {
    const res = await apiClient.post<{ project: any }>(`/rfq/projects/${projectId}/updates`, input);
    if (!res.success) throw new Error(res.error.message);
    return mapProject(res.data.project);
  },

  /** PATCH /rfq/projects/:id/timeline — designer ticks off production stages */
  async markStages(projectId: string, doneStages: string[]): Promise<Project> {
    const res = await apiClient.patch<{ project: any }>(`/rfq/projects/${projectId}/timeline`, { doneStages });
    if (!res.success) throw new Error(res.error.message);
    return mapProject(res.data.project);
  },

  /** POST /rfq/projects/:id/mark-delivered — designer marks the outfit as delivered */
  async markDelivered(projectId: string): Promise<Project> {
    const res = await apiClient.post<{ project: any }>(`/rfq/projects/${projectId}/mark-delivered`);
    if (!res.success) throw new Error(res.error.message);
    return mapProject(res.data.project);
  },

  /** POST /rfq/projects/:id/confirm-delivery — buyer confirms receipt, releases escrow */
  async confirmDelivery(projectId: string): Promise<Project> {
    const res = await apiClient.post<{ project: any }>(`/rfq/projects/${projectId}/confirm-delivery`);
    if (!res.success) throw new Error(res.error.message);
    return mapProject(res.data.project);
  },

  /** POST /rfq/projects/:id/request-extension */
  async requestExtension(projectId: string, input: RequestExtensionInput): Promise<Project> {
    const res = await apiClient.post<{ project: any }>(`/rfq/projects/${projectId}/request-extension`, input);
    if (!res.success) throw new Error(res.error.message);
    return mapProject(res.data.project);
  },

  /**
   * POST /rfq/projects/:id/issues — buyer raises a dispute.
   * Expected backend behaviour: persist the issue, pause the balance auto-release,
   * mark the project status as `disputed`, post a system card into the order
   * thread, and notify support for mediation.
   */
  async raiseProjectIssue(
    projectId: string,
    input: { reason: string; description: string },
  ): Promise<void> {
    const res = await apiClient.post(`/rfq/projects/${projectId}/issues`, input);
    if (!res.success) {
      const err = new Error(res.error.message) as Error & { appCode?: string };
      err.appCode = res.error.appCode;
      throw err;
    }
  },

  /** PATCH /rfq/projects/:id/extension — buyer approves or rejects a deadline extension */
  async respondToExtension(projectId: string, action: 'accept' | 'reject'): Promise<Project> {
    const res = await apiClient.patch<{ project: any }>(`/rfq/projects/${projectId}/extension`, { action });
    if (!res.success) throw new Error(res.error.message);
    return mapProject(res.data.project);
  },

  // ── File uploads ────────────────────────────────────────────────────

  /** POST /rfq/uploads/brief-images — upload style refs / fabric photos before submitting */
  async uploadBriefImages(files: File[]): Promise<string[]> {
    const form = new FormData();
    files.forEach(f => form.append('images', f));
    const res = await apiClient.uploadFile<UploadMediaResponse>('/rfq/uploads/brief-images', form);
    if (!res.success) throw new Error(res.error.message);
    return res.data.urls;
  },

  /** POST /rfq/uploads/project-media — upload progress photos/video for a project update */
  async uploadProjectMedia(files: File[]): Promise<string[]> {
    const form = new FormData();
    files.forEach(f => form.append('media', f));
    const res = await apiClient.uploadFile<UploadMediaResponse>('/rfq/uploads/project-media', form);
    if (!res.success) throw new Error(res.error.message);
    return res.data.urls;
  },
};
